import { useAnalyticContext } from '@application/analytic/analytic_context.provider';
import { applicationLocalStorage } from '@application/application_storage_local';
import { applicationSessionStorage } from '@application/application_storage_session';
import { useUser } from '@application/authentication/use_user';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { useTimeSlots } from '@feature/time_slot/use_time_slots';
import { useMutation } from '@tanstack/react-query';
import { basketDeleteActiveBasketMutation } from '@visiba/api-vcs-client-v2';
import { ENDPOINTS, client as vcsGeneralClient } from '@visiba/api-vcs-general';
import { useResolve } from '@visiba/instantiation';

const EMPTY_REPRESENTS_ID = '00000000-0000-0000-0000-000000000000';

const getCurrentReturnUrl = () => {
	const returnUrl = new URL(window.location.href);
	returnUrl.searchParams.set('openInApp', 'false');

	return returnUrl.toString();
};

export const usePatientRepresentation = () => {
	const user = useUser();
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);
	const timeSlots = useTimeSlots();
	const deleteActiveBasket = useMutation(basketDeleteActiveBasketMutation());
	const remoteScheme = user?.remoteLoginScheme?.toLowerCase();
	const returnUrl = getCurrentReturnUrl();
	const analytic = useAnalyticContext();
	const isLoading = remoteScheme == null;

	const updateRepresentation = (id: string) => {
		if (isLoading) {
			return;
		}

		const url = vcsGeneralClient.buildUrl({
			url: ENDPOINTS.IdpAuthenticationChallengeClient,
			path: {
				licenceHolderId: universalConfig.licenceHolderId,
				authType: 'client',
				remoteScheme,
			},
			query: {
				returnUrl,
				countryCode: universalConfig.selectedCountryCode.value,
				languageCode: universalConfig.selectedLanguageCode.value,
				representsId: id,
			},
		});

		window.location.assign(url);
	};

	const startRepresentation = (id: string) => {
		analytic.trackEvent(`Started representing patient "${id}"`);
		return updateRepresentation(id);
	};

	const stopRepresentation = async () => {
		if (isLoading) return;

		const baskets = applicationSessionStorage.get('bookingBasket') ?? {};
		const bookingSettings = applicationLocalStorage.get('bookingSettings') ?? {};
		const messageSettings = applicationLocalStorage.get('createNewMessageSettings') ?? {};
		const currentReceptionId = unitService.getUnitNodeInTree(universalConfig.selectedUnitId).receptionId;
		const receptionIds = new Set(
			[currentReceptionId, ...Object.keys(baskets), ...Object.keys(bookingSettings), ...Object.keys(messageSettings)]
				.map(Number)
				.filter((id) => id > 0),
		);
		for (const receptionId of receptionIds) {
			try {
				await deleteActiveBasket.mutateAsync({ path: { receptionId } });
			} catch (error) {
				if (!(typeof error === 'object' && error !== null && 'ErrorCode' in error && error.ErrorCode === 'NotFound_NoBasket')) {
					throw error;
				}
			}
		}

		const formAnswers = applicationSessionStorage.get('dynamicForms') ?? {};
		for (const [receptionId, basket] of Object.entries(baskets)) {
			if (basket?.timeSlotLockId) {
				try {
					const unitId = unitService.getUnitNodeInTreeByReceptionId(Number(receptionId)).id;
					await timeSlots.deleteLockTimeSlot(unitId, basket.timeSlotLockId);
				} catch {
					// An expired lock or removed reception must not prevent the draft from being cleared.
				}
			}
			if (basket?.formRespondantId) {
				delete formAnswers[basket.formRespondantId];
			}
		}
		applicationSessionStorage.set('dynamicForms', formAnswers);
		applicationSessionStorage.removeItem('bookingBasket');
		applicationSessionStorage.removeItem('reschedulingReason');
		applicationLocalStorage.removeItem('bookingSettings');
		applicationLocalStorage.removeItem('createNewMessageSettings');

		analytic.trackEvent(`Stopped Representing patient"`);
		return updateRepresentation(EMPTY_REPRESENTS_ID);
	};

	return { startRepresentation, stopRepresentation, isLoading };
};
